import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../../firebase";

import {
  issueAgainstMR,
  validateAvailableStock
} from "../../services/inventoryService";

import {
  buildInventoryMap,
  getItemPrimaryKey
} from "../../utils/inventoryKeyResolver";

import {
  doc,
  updateDoc,
  serverTimestamp,
  collection,
  addDoc,
  query,
  where,
  getDocFromServer,
  getDocsFromServer
} from "firebase/firestore";

import { createTransferRequest, HOLDER_TYPE } from "../../services/assets/assetTransactionService";
import { markMRFIssued, checkStageVariations, recordVariationsAndAdjustBudget } from "../../pages/projects/projectMaterialService";
import { notifyVariationRecorded } from "../../services/notificationService";

const normalizeStatus = (status = "") => {
  const normalized = String(status).toLowerCase().trim().replace(/\s+/g, "_");
  if (normalized === "partial")  return "partially_issued";
  if (normalized === "rejected") return "cancelled";
  return normalized;
};

export default function StoreMRFDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [type, setType] = useState("N/A");

  const [mrf,          setMrf]          = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [siteName,     setSiteName]     = useState("Site Not Available");
  // Canonical resolved site_id (Site-wise Stock Tracking, Phase 1) — kept
  // SEPARATE from siteName (which is a display-only string). For
  // project-linked MRFs, siteName was already correctly resolved via
  // project.site_id → sites/{id}, but the resolved ID itself was never
  // kept — only the name. site_inventory_summary needs the real ID, not
  // a display string, so it's captured here alongside siteName.
  const [resolvedSiteId, setResolvedSiteId] = useState("");
  const [inventoryMap, setInventoryMap] = useState({});
  const [issuing,      setIssuing]      = useState(false);
  const [issuingTools, setIssuingTools] = useState(false);
  const [techTools,    setTechTools]    = useState([]);

  // ── Stage Variation pre-check (Task B) ──────────────────────────────────
  const [showVariationModal, setShowVariationModal] = useState(false);
  const [pendingVariations,  setPendingVariations]  = useState([]);
  const [variationForm,      setVariationForm]      = useState({
    approved_by_name: "", approval_channel: "phone", approval_note: "",
  });
  const [variationSaving,    setVariationSaving]    = useState(false);

  const refreshMRFScreen = useCallback(async () => {
    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const companyId = user.company_id || "";
      const [mrfSnap, inventorySnap] = await Promise.all([
        getDocFromServer(doc(db, "material_requests", id)),
        getDocsFromServer(query(collection(db, "inventory_summary"), where("company_id", "==", companyId))),
      ]);

      if (mrfSnap.exists()) {
        const data        = mrfSnap.data();
        if (data.company_id !== companyId) {
          alert("Access denied — this MRF belongs to a different company");
          navigate("/store-mrf");
          return;
        }
        const safeStatus  = normalizeStatus(data.status);
        const sourceItems = Array.isArray(data.items) ? data.items : [];

        const updatedItems = sourceItems.map(item => ({
          ...item,
          category:     item.category || "",
          qty:          Number(item.qty || 0),
          approved_qty: item.approved_qty !== undefined
            ? Number(item.approved_qty)
            : Number(item.qty || 0),
          issued_qty:   Math.max(0, Number(
            item.issued_qty || item.issuedQuantity || item.issue_qty || item.issued || 0
          )),
          remaining_qty: Math.max(0, Number(item.remaining_qty || 0)),
        }));

        setMrf({ id: mrfSnap.id, ...data, status: safeStatus, items: updatedItems });
        setType(data.type || (data.project_id ? "project" : "service"));
        fetchSiteName(data.project_id, data.site_name, data.site_id);
        fetchTechTools(data.receiver_id, data.company_id);
      }

      const inventoryList = inventorySnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setInventoryMap(buildInventoryMap(inventoryList));

    } catch (error) {
      console.error("MRF Refresh Error:", error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { refreshMRFScreen(); }, [refreshMRFScreen]);

  const fetchTechTools = async (receiver_id, company_id) => {
    if (!receiver_id || !company_id) return;
    try {
      const snap = await getDocsFromServer(query(
        collection(db, "asset_master"),
        where("company_id",        "==", company_id),
        where("current_holder_id", "==", receiver_id)
      ));
      setTechTools(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.warn("[StoreMRFDetail] fetchTechTools failed:", e);
    }
  };

  const fetchSiteName = async (project_id, fallback_site_name, fallback_site_id) => {
if (!project_id) {
  // Standalone MRF — no project, so mrf.site_id (if present) IS the
  // canonical site. This is the only path where fallback_site_id is
  // trustworthy as-is.
  setSiteName(fallback_site_name || "Site Not Available");
  setResolvedSiteId(fallback_site_id || "");
  return;
}
try {
const { doc: fsDoc, getDoc } = await import("firebase/firestore");
const projSnap = await getDoc(fsDoc(db, "projects", project_id));
const projectSiteId = projSnap.exists() ? projSnap.data().site_id : "";
if (!projectSiteId) {
  setSiteName(fallback_site_name || "Site Not Available");
  setResolvedSiteId(fallback_site_id || "");
  return;
}
const siteSnap = await getDoc(fsDoc(db, "sites", projectSiteId));
setSiteName(siteSnap.exists() ? (siteSnap.data().site_name || fallback_site_name || "Site Not Available") : (fallback_site_name || "Site Not Available"));
// Project-linked MRF — the real site is the PROJECT's site, always,
// even if mrf.site_id (fallback) disagrees or is empty. This is the
// fix: previously this resolved ID was never captured anywhere.
setResolvedSiteId(projectSiteId);
} catch {
setSiteName(fallback_site_name || "Site Not Available");
setResolvedSiteId(fallback_site_id || "");
}
};

  const handleItemChange = (index, field, value) => {
    let val = parseInt(value);
    if (isNaN(val) || val < 0) val = 0;
    const updated = [...mrf.items];
    updated[index][field] = val;
    setMrf({ ...mrf, items: updated });
  };

  const handleApprove = async () => {
    try {
      const updatedItems = mrf.items.map(item => {
        const cat         = String(item.category || "").trim().toUpperCase();
        const approvedQty = item.approved_qty !== undefined
          ? Number(item.approved_qty)
          : Number(item.qty || 0);
        const issuedQty   = Number(item.issued_qty || 0);
        const remaining   = Math.max(0, approvedQty - issuedQty);

        if (cat === "TOOLS" || cat === "ASSET") {
          return { ...item, approved_qty: approvedQty };
        }

        // ── Already redirected to Asset Registry — do not overwrite ──
        if (item.item_status === "redirected_to_asset_registry") {
          return { ...item };
        }

        if (!item.item_id || !String(item.item_id).trim()) {
          return {
            ...item,
            approved_qty:     approvedQty,
            issued_qty:       issuedQty,
            remaining_qty:    remaining,
            item_status:      "procurement_pending",
            procurement_type: item.procurement_type || "purchase",
          };
        }

        const strictItemId   = String(item.item_id || "").trim();
        const availableStock = Number(inventoryMap[strictItemId] || 0);

        const autoStatus = issuedQty >= approvedQty
          ? "issued"
          : availableStock <= 0
          ? "procurement_pending"
          : "approved_pending_issue";

        return {
          ...item,
          approved_qty:     approvedQty,
          issued_qty:       issuedQty,
          remaining_qty:    remaining,
          item_status:      autoStatus,
          procurement_type: "purchase",
        };
      });

      // D-299: record who approved — the "Approved By" line on this screen
      // reads approved_by_name, which was never written. Also feeds D-297.
      const approver = JSON.parse(localStorage.getItem("user") || "{}");
      await updateDoc(doc(db, "material_requests", id), {
        status:           "approved",
        items:            updatedItems,
        approved_by:      approver.uid  || "",
        approved_by_name: approver.name || approver.email || "",
        approved_by_role: approver.role || "",
        approved_at:      serverTimestamp(),
        updated_at:       serverTimestamp(),
      });

      try {
        const { markNotificationReadByReference } = await import("../../services/notificationService");
        await markNotificationReadByReference(id, "mrf_pending", mrf.company_id || "");
      } catch (e) {
        console.warn("[handleApprove] resolve notification failed (non-blocking):", e);
      }

      await refreshMRFScreen();
      const procPending = updatedItems.filter(i => i.item_status === "procurement_pending").length;
      const issuable    = updatedItems.filter(i => i.item_status === "approved_pending_issue").length;

      if (procPending > 0 && issuable > 0) {
        alert(`MRF Approved.\n${procPending} item(s) sent to Procurement Queue.\n${issuable} item(s) ready to issue from Issue Material.`);
      } else if (procPending > 0) {
        alert(`MRF Approved. ${procPending} item(s) sent to Procurement Queue (stock not available).`);
      } else {
        alert(`MRF Approved. Use the Issue Material button to issue stock.`);
      }
    } catch (error) {
      console.error("Approve Error:", error);
      alert(error.message || "Approve Failed");
    }
  };

  const handleReject = async () => {
    try {
      // D-299: record who rejected.
      const rejecter = JSON.parse(localStorage.getItem("user") || "{}");
      await updateDoc(doc(db, "material_requests", id), {
        status:            "cancelled",
        cancelled_by:      rejecter.uid  || "",
        cancelled_by_name: rejecter.name || rejecter.email || "",
        cancelled_by_role: rejecter.role || "",
        cancelled_at:      serverTimestamp(),
        updated_at:        serverTimestamp(),
      });
      await refreshMRFScreen();
      alert("MRF Cancelled");
    } catch (error) {
      console.error("Reject Error:", error);
      alert(error.message || "Reject Failed");
    }
  };

  const handleIssueTool = async () => {
    if (issuingTools) return;

    const toolItems = (mrf?.items || []).filter(i =>
      i.is_tool === true ||
      String(i.category || "").trim().toUpperCase() === "TOOLS"
    );
    if (toolItems.length === 0) return;

    const receiverId   = mrf.receiver_id   || "";
    const receiverName = mrf.receiver_name || mrf.requested_by_name || "";
    const receiverRole = mrf.receiver_role || "staff";
    const receiverType = mrf.receiver_type || "user";

    if (!receiverId) { alert("No receiver found on this tool request."); return; }

    try {
      setIssuingTools(true);
      const user   = JSON.parse(localStorage.getItem("user") || "{}");
      const errors = [];

      for (const item of toolItems) {
        try {
          const toType = receiverType === "site"
            ? HOLDER_TYPE.SITE
            : receiverRole === "technician"
            ? HOLDER_TYPE.TECHNICIAN
            : HOLDER_TYPE.STAFF;

          await createTransferRequest({
            asset_id:          item.item_id,
            from_type:         item.current_holder_type || HOLDER_TYPE.STORE,
            from_id:           item.current_holder_id || "MAIN_STORE",
            from_holder_name:  item.current_holder_name || "Store",
            to_type:           toType,
            to_id:             receiverId,
            to_holder_name:    receiverName,
            requested_by:      user.uid || user.id || "",
            requested_by_name: user.name || "",
            remarks: mrf.remarks || (mrf.service_task_id
              ? `Service: ${mrf.service_task_id}`
              : "MRF Tool Issue"),
            company_id: mrf.company_id || "",
          });
        } catch (err) {
          errors.push(`${item.item_name}: ${err.message}`);
        }
      }

      const allIssued = errors.length === 0;
      await updateDoc(doc(db, "material_requests", id), {
        status:     allIssued ? "issued" : "partially_issued",
        updated_at: serverTimestamp(),
      });
      await refreshMRFScreen();

      if (errors.length === 0) {
        alert(`✅ ${toolItems.length} tool(s) transfer initiated to ${receiverName}`);
      } else {
        alert(`⚠ Partial:\n${errors.join("\n")}`);
      }
    } catch (err) {
      console.error("Tool issue error:", err);
      alert(err.message || "Tool issue failed");
    } finally {
      setIssuingTools(false);
    }
  };

  const handleReminderDone = async () => {
    try {
      await updateDoc(doc(db, "material_requests", id), {
        reminder_status: "done", updated_at: serverTimestamp(),
      });
      await refreshMRFScreen();
    } catch (error) {
      console.error("Reminder Done Error:", error);
    }
  };

  const handleRemindLater = async () => { alert("Reminder postponed"); };

  const performIssue = async () => {
    if (issuing) return;
    setIssuing(true);
    try {
      // D-299: the real store user — was the literal "store" before.
      const issuer = JSON.parse(localStorage.getItem("user") || "{}");
      const baseItems       = mrf?.items || [];
      const updatedMRFItems = JSON.parse(JSON.stringify(baseItems));
      let anyIssued = false;

      for (const item of updatedMRFItems) {
        const itemId = getItemPrimaryKey(item);

        if (!itemId) {
          if (item.is_custom_item) {
            item.item_status = "procurement_pending";
            continue;
          }
          throw new Error(`Invalid item_id for ${item.item_name || "Unknown Item"}`);
        }

        const normalizedCategory = String(item.category || "").trim().toUpperCase();
        if (normalizedCategory === "TOOLS" || normalizedCategory === "ASSET") {
          console.log("TOOL/ASSET ISSUE BLOCKED:", item.item_name);
          continue;
        }

        const approved      = item.approved_qty !== undefined ? Number(item.approved_qty) : Number(item.qty || 0);
        const alreadyIssued = Number(item.issued_qty || item.issuedQuantity || item.issue_qty || item.issued || 0);
        const pendingQty    = Math.max(0, approved - alreadyIssued);

        if (pendingQty <= 0) continue;

        const strictItemId   = String(item.item_id || "").trim();
        const availableStock = Number(inventoryMap[strictItemId] || 0);

        let issueQty = 0;
        if (availableStock >= pendingQty)  issueQty = pendingQty;
        else if (availableStock > 0)       issueQty = availableStock;

        const remainingQty = approved - (alreadyIssued + issueQty);

        if (issueQty <= 0) {
          const itemIndex = updatedMRFItems.findIndex(
            x => String(getItemPrimaryKey(x)) === String(itemId)
          );
          if (itemIndex >= 0) {
            updatedMRFItems[itemIndex] = {
              ...updatedMRFItems[itemIndex],
              approved_qty:     approved,
              issued_qty:       alreadyIssued,
              remaining_qty:    pendingQty,
              item_status:      alreadyIssued > 0 ? "partially_issued" : "procurement_pending",
              procurement_type: updatedMRFItems[itemIndex].procurement_type || "purchase",
            };
          }
          continue;
        }

        await issueAgainstMR({
  company_id:    mrf.company_id || "",
  item_id:       itemId,
  item_code:     item.item_code || "",
  item_name:     item.item_name || "",
  approved_qty:  approved,
  issued_qty:    alreadyIssued + issueQty,
  remaining_qty: remainingQty,
  category:      item.category || "",
  qty:           Number(issueQty),
  unit:          item.unit || item.uom || "nos",
  reference_id:  mrf.id,
  reference_type:"MRF",
  project_id:    mrf.project_id || "",
  project_name:  mrf.project_name || "",
  // Site-wise Stock Tracking (Phase 1): resolvedSiteId is the canonical
  // site (project.site_id for project MRFs; standalone MRF's own
  // site_id otherwise) — mrf.site_id kept ONLY as a last-resort fallback
  // for the rare case resolvedSiteId never got set.
  site_id:       resolvedSiteId || mrf.site_id || "",
  site_name:     mrf.site_name  || siteName || "",
  issued_by:        issuer.uid || "",
  issued_by_name:   issuer.name || issuer.email || "",
  received_by:      mrf.requested_by || "",
  received_by_name: mrf.requested_by_name || "",
  remarks:       "MR Material Issue",
});

        await addDoc(collection(db, "material_issues"), {
          company_id:      mrf.company_id || "",
          issue_id:        `${mrf.id}_${itemId}_${Date.now()}`,
          mrf_id:          mrf.id,
          site_id:         resolvedSiteId || mrf.site_id || "",
          site_name:       mrf.site_name || siteName || "",
          project_id:      mrf.project_id || "",
          technician_id:   mrf.technician_id || "",
          technician_name: mrf.requested_by_name || "",
          issue_type:      type || "service",
          issued_by:       issuer.uid || "",
          issued_by_name:  issuer.name || issuer.email || "",
          issued_by_uid:   issuer.uid || "",
          issued_at:       serverTimestamp(),
          created_at:      serverTimestamp(),
          status:          remainingQty <= 0 ? "issued" : "partially_issued",
          returnable:      true,
          items: [{
            item_id:       itemId,
            item_code:     item.item_code || "",
            item_name:     item.item_name || "",
            category:      item.category  || "",
            unit:          item.unit || item.uom || "nos",
            requested_qty: Number(item.qty || 0),
            approved_qty:  approved,
            issued_qty:    issueQty,
            pending_qty:   remainingQty,
            returned_qty:  0,
            defective_qty: 0,
          }],
          remarks:          "",
          reminder_status:  "pending",
        });

        const itemIndex = updatedMRFItems.findIndex(
          x => String(getItemPrimaryKey(x)) === String(itemId)
        );
        if (itemIndex >= 0) {
          updatedMRFItems[itemIndex] = {
            ...updatedMRFItems[itemIndex],
            approved_qty:     approved,
            issued_qty:       alreadyIssued + issueQty,
            remaining_qty:    remainingQty,
            item_status:      remainingQty <= 0 ? "issued" : "partially_issued",
            procurement_type: remainingQty > 0
                                ? (updatedMRFItems[itemIndex].procurement_type || "purchase")
                                : updatedMRFItems[itemIndex].procurement_type,
          };
        }
        anyIssued = true;
      }

      const issueRelevantItems = updatedMRFItems.filter(i => Number(i.approved_qty || 0) > 0);
      const totalApproved      = issueRelevantItems.reduce((s, i) => s + Number(i.approved_qty || 0), 0);
      const totalIssued        = issueRelevantItems.reduce((s, i) => s + Number(i.issued_qty   || 0), 0);

      let finalStatus = "approved";
      if (totalApproved > 0 && totalIssued >= totalApproved) finalStatus = "issued";
      else if (totalIssued > 0) finalStatus = "partially_issued";

      if (anyIssued && mrf.project_id) {
        // Declared here (function-level, not inside try{}) so the catch
        // block below can safely reference it in its error message even
        // if something fails before this reaches its assignment further
        // down — was previously a `const` inside try{} only, which is
        // out of scope in catch{}, causing a build-blocking 'not defined'
        // ESLint error.
        let issued_items = [];
        try {
          const { getDocs, query, where, collection: fsCollection } = await import("firebase/firestore");
          const stagesSnap = await getDocs(query(
            fsCollection(db, "project_material_stages"),
            where("company_id", "==", mrf.company_id || ""),
            where("project_id", "==", mrf.project_id)
          ));
          if (!stagesSnap.empty) {
            const issuedItemIds = updatedMRFItems
              .filter(i => Number(i.issued_qty || 0) > 0)
              .map(i => String(i.item_id || "").trim());

            // Find EVERY stage that has at least one issued item in its
            // budget — a single MRF can issue items that belong to more
            // than one stage (e.g. a shared item used in both Piping-High
            // and Piping-Low). Updating only the first match silently
            // skipped every other matching stage's quota tracking.
            const matchedStageIds = [];
            for (const stageDoc of stagesSnap.docs) {
              const stageData = stageDoc.data();
              const budgetIds = (stageData.material_budget || []).map(b => String(b.item_id || "").trim());
              const hasMatch  = issuedItemIds.some(id => budgetIds.includes(id));
              if (hasMatch) matchedStageIds.push(stageDoc.id);
            }

            if (matchedStageIds.length > 0) {
              issued_items = updatedMRFItems
                .filter(i => Number(i.issued_qty || 0) > 0)
                .map(i => ({
                  item_id:    String(i.item_id || "").trim(),
                  item_name:  i.item_name || "",
                  qty_issued: Number(i.issued_qty || 0),
                }));
              const user = JSON.parse(localStorage.getItem("user") || "{}");
              // This whole page (StoreMRFDetail.js) only ever loads its MRF
              // doc from the material_requests collection — see
              // refreshMRFScreen()'s getDocFromServer call above, which is
              // the ONLY way this page ever successfully displays an MRF.
              // The previous `mrf.source === "project_stage" ?
              // "material_requests" : "project_mrfs"` check was wrong for
              // any MRF created via the general "Create Material Request"
              // page (which never sets source: "project_stage") — it tried
              // updating a document that doesn't exist in project_mrfs,
              // guaranteed to fail every time for such MRFs.
              for (const matchedStageId of matchedStageIds) {
                await markMRFIssued(id, matchedStageId, issued_items, user, "material_requests");
              }
            }
          }
        } catch (budgetErr) {
          console.error("[TASK1] Budget sync failed:", budgetErr);
          // Costing (issueAgainstMR, above in this same loop) already
          // recorded the cost — but if THIS stage-quota sync fails, the
          // manager has no other way to know the two have drifted apart.
          // Silently logging it hides exactly the risk we're fixing.
          alert(
            "Material was issued and the cost was recorded, but updating " +
            "the Material Control stage's Issued quantity failed.\n\n" +
            "To fix manually: open the project's Material Control page → " +
            "find the stage this item belongs to → use its ✏️ Edit " +
            "Triggers / stage edit option, or adjust the stage's Issued " +
            "quantity directly in Firestore (project_material_stages → " +
            "material_budget[] → the matching item_id's qty_issued) to " +
            "reflect that " + (issued_items?.[0]?.qty_issued ?? "the") +
            " unit(s) were actually issued.\n\nError detail: " +
            (budgetErr?.message || String(budgetErr))
          );
        }
      }

      await updateDoc(doc(db, "material_requests", id), {
        items: updatedMRFItems, status: finalStatus, updated_at: serverTimestamp(),
      });

      await refreshMRFScreen();
      alert(anyIssued ? "Material Issued" : "No Stock Available");

    } catch (error) {
      console.error("Issue Error:", error);
      alert(error.message || "Issue Failed");
    } finally {
      setIssuing(false);
    }
  };

  // Pre-flight check — runs BEFORE performIssue() ever touches
  // issueAgainstMR(). If nothing would exceed any stage's budget, goes
  // straight through with no visible change in behavior. A pre-check
  // failure (e.g. network hiccup) is non-blocking — logs and proceeds,
  // same "never let a secondary check silently block the real issue"
  // discipline as the existing stage-quota-sync error handling below.
  const handleIssue = async () => {
    if (issuing) return;
    if (!mrf?.project_id) { await performIssue(); return; }

    try {
      const itemsToCheck = (mrf.items || [])
        .filter(item => {
          const cat = String(item.category || "").trim().toUpperCase();
          if (cat === "TOOLS" || cat === "ASSET") return false;
          const approved      = item.approved_qty !== undefined ? Number(item.approved_qty) : Number(item.qty || 0);
          const alreadyIssued = Number(item.issued_qty || 0);
          return (approved - alreadyIssued) > 0;
        })
        .map(item => ({
          item_id:   String(item.item_id || "").trim(),
          item_name: item.item_name || "",
          unit:      item.unit || item.uom || "nos",
          issue_qty: (item.approved_qty !== undefined ? Number(item.approved_qty) : Number(item.qty || 0))
                      - Number(item.issued_qty || 0),
        }));

      const exceedList = await checkStageVariations(mrf.company_id || "", mrf.project_id, itemsToCheck);
      if (exceedList.length > 0) {
        setPendingVariations(exceedList);
        setVariationForm({ approved_by_name: "", approval_channel: "phone", approval_note: "" });
        setShowVariationModal(true);
        return; // wait for Manager to confirm in the modal
      }
    } catch (err) {
      console.warn("[handleIssue] variation pre-check failed (non-blocking, proceeding with issue):", err);
    }

    await performIssue();
  };

  const handleConfirmVariation = async () => {
    if (!variationForm.approved_by_name.trim() || !variationForm.approval_note.trim()) {
      alert("Please provide who approved this and a note before proceeding.");
      return;
    }
    try {
      setVariationSaving(true);
      const user    = JSON.parse(localStorage.getItem("user") || "{}");
      const summary = await recordVariationsAndAdjustBudget(pendingVariations, variationForm, user);

      for (const s of summary) {
        try {
          await notifyVariationRecorded({
            ...s, company_id: mrf.company_id || "",
            recorded_by_name: user.name || user.email || "",
          });
        } catch (e) {
          console.warn("[handleConfirmVariation] notification failed (non-blocking):", e);
        }
      }

      setShowVariationModal(false);
      setPendingVariations([]);
      await performIssue();
    } catch (err) {
      alert(err.message || "Failed to record variation");
    } finally {
      setVariationSaving(false);
    }
  };

  if (loading) return <p style={{ padding: 20 }}>Loading...</p>;
  if (!mrf)    return <p style={{ padding: 20 }}>No Data</p>;

  return (
    <div style={{ padding: 28, background: "#f4f6f8", minHeight: "100vh" }}>
      <button
        onClick={() => navigate("/store-mrf")}
        style={{
          marginBottom: 20, padding: "10px 18px",
          background: "#ffffff", border: "1px solid #d1d5db",
          borderRadius: 10, cursor: "pointer", fontWeight: 600,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}>
        ← Back to MRF Dashboard
      </button>

      <div style={card}>
        <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 16, color: "#111827" }}>MRF Detail</h2>

        {/* ── INFO GRID ── */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px 32px", marginBottom:20 }}>
          {[
            { l:"Status",       v: <span style={statusBadge(mrf?.status||"pending")}>{(mrf?.status||"pending").replace(/_/g," ")}</span> },
            { l:"Requested By", v: mrf.requested_by_name || mrf.requested_by || "—" },
            { l:"Requested On", v: mrf.created_at ? (mrf.created_at.toDate ? mrf.created_at.toDate() : new Date(mrf.created_at)).toLocaleString("en-IN",{day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"}) : "—" },
            { l:"Type",         v: type },
            { l:"Site",         v: (!siteName || siteName==="Site Not Available") ? `⚠ ${mrf.site_name||mrf.site_id||"Site Not Linked"}` : siteName },
            mrf.mrf_no           ? { l:"MRF No",      v: mrf.mrf_no }           : null,
            mrf.project_name     ? { l:"Project",     v: mrf.project_name }     : null,
            mrf.approved_by_name ? { l:"Approved By", v: mrf.approved_by_name } : null,
          ].filter(Boolean).map((row, i) => (
            <div key={i} style={{ display:"flex", flexDirection:"column", gap:3, padding:"10px 14px", background:"#F9FAFB", borderRadius:10, border:"1px solid #F1F1F1" }}>
              <span style={{ fontSize:11, fontWeight:600, color:"#9CA3AF", textTransform:"uppercase", letterSpacing:"0.05em" }}>{row.l}</span>
              <span style={{ fontSize:14, fontWeight:700, color:"#111827" }}>{row.v}</span>
            </div>
          ))}
        </div>

        {/* ✅ Flow-based Next Step Navigation */}
        {(() => {
          const hasProcurement = (mrf?.items || []).some(i => i.item_status === "procurement_pending");
          const hasIssuable    = (mrf?.items || []).some(i => i.item_status === "approved_pending_issue" || i.item_status === "partially_issued");
          if (!hasProcurement && !hasIssuable && mrf.status !== "approved") return null;
          return (
            <div style={{ marginTop:12, padding:"12px 16px", background:"#f0f9ff", border:"1px solid #bae6fd", borderRadius:12, display:"flex", gap:10, flexWrap:"wrap", alignItems:"center" }}>
              <span style={{ fontSize:12, color:"#0369a1", fontWeight:600 }}>Next Steps:</span>
              {hasProcurement && (
                <>
                  {(mrf?.items || []).some(i => i.is_custom_item && i.item_status === "procurement_pending") && (
                    <div style={{ width:"100%", padding:"8px 12px", background:"#fef9c3", border:"1px solid #fde68a", borderRadius:8, fontSize:11, color:"#92400e", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8 }}>
                      <span>⚠ Custom items need to be approved in Item Requests before procurement.</span>
                      <button onClick={() => navigate("/item-requests")} style={{ padding:"4px 10px", background:"#92400e", color:"#fff", border:"none", borderRadius:6, fontSize:11, fontWeight:600, cursor:"pointer" }}>📋 Item Requests →</button>
                    </div>
                  )}
                  <button onClick={() => navigate("/store-procurement")} style={{ padding:"8px 14px", background:"#f59e0b", color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:600, fontSize:12 }}>📦 Procurement Queue →</button>
                  <button onClick={() => navigate("/purchase-orders")}  style={{ padding:"8px 14px", background:"#7c3aed", color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:600, fontSize:12 }}>🛒 Purchase Orders →</button>
                </>
              )}
              {hasIssuable && (
                <button onClick={handleIssue} style={{ padding:"8px 14px", background:"#2563eb", color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:600, fontSize:12 }}>✅ Issue Material →</button>
              )}
            </div>
          );
        })()}

        {mrf.request_type === "tool" && (
          <>
            <p><b>Requested by:</b>{" "}
              <span style={{ background:"#ede9fe", color:"#5b21b6", padding:"3px 10px", borderRadius:20, fontWeight:600, fontSize:13 }}>{mrf.requested_by_name || "—"}</span>
              {mrf.site_name && <span style={{ marginLeft:8, color:"#64748b", fontSize:13 }}>— {mrf.site_name}</span>}
            </p>
            <p><b>Issue To:</b>{" "}
              <span style={{ background:"#ede9fe", color:"#5b21b6", padding:"3px 10px", borderRadius:20, fontWeight:600, fontSize:13 }}>{mrf.receiver_name || "—"} ({mrf.receiver_role || "staff"})</span>
            </p>

            {/* ── TECH CURRENT TOOLS — next-step visibility ── */}
            <div style={{ marginTop:16, padding:"14px 16px", background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:12 }}>
              <div style={{ fontSize:13, fontWeight:700, color:"#374151", marginBottom:10 }}>
                🧰 {mrf.receiver_name || "Tech"}'s Current Tools ({techTools.length})
              </div>
              {techTools.length === 0 ? (
                <div style={{ fontSize:12, color:"#6b7280" }}>No tools currently assigned — safe to transfer.</div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  {techTools.map(t => (
                    <div key={t.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"6px 10px", background:"#fff", border:"1px solid #e5e7eb", borderRadius:8 }}>
                      <div>
                        <span style={{ fontSize:13, fontWeight:600, color:"#111827" }}>{t.asset_name}</span>
                        {t.serial_no && <span style={{ fontSize:11, color:"#9ca3af", marginLeft:8 }}>#{t.serial_no}</span>}
                        {t.asset_category && <span style={{ fontSize:11, color:"#6b7280", marginLeft:8 }}>{t.asset_category}</span>}
                      </div>
                      <span style={{
                        fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:20,
                        background: t.condition === "WORKING" ? "#dcfce7" : "#fee2e2",
                        color:      t.condition === "WORKING" ? "#166534" : "#991b1b",
                      }}>
                        {t.condition || "WORKING"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div style={card}>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead>
            <tr style={{ background:"#f8fafc", fontSize:14 }}>
              <th style={{ padding:"10px 16px", textAlign:"left", fontWeight:600, color:"#6B7280" }}>Item</th>
              <th style={{ padding:"10px 16px", textAlign:"left", fontWeight:600, color:"#6B7280" }}>Unit</th>
              <th style={{ padding:"10px 16px", textAlign:"left", fontWeight:600, color:"#6B7280" }}>Requested</th>
              <th style={{ padding:"10px 16px", textAlign:"left", fontWeight:600, color:"#6B7280" }}>Approved</th>
              <th style={{ padding:"10px 16px", textAlign:"left", fontWeight:600, color:"#6B7280" }}>Stock</th>
              <th style={{ padding:"10px 16px", textAlign:"left", fontWeight:600, color:"#6B7280" }}>Issued</th>
              <th style={{ padding:"10px 16px", textAlign:"left", fontWeight:600, color:"#6B7280" }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {(mrf?.items || []).map((item, i) => (
              <tr key={i}>
                <td style={td}>
                  <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-start", gap:6 }}>
                      <span style={{ fontWeight:500, color:"#111827" }}>{item.item_name}</span>
                      {["TOOLS","ASSET"].includes(String(item.category||"").trim().toUpperCase()) && (
                        <span style={{ background:"#dbeafe", color:"#1d4ed8", padding:"2px 8px", borderRadius:20, fontSize:10, fontWeight:700, letterSpacing:0.5 }}>ASSET TRACKING</span>
                      )}
                      {["MATERIAL","CONSUMABLE"].includes(String(item.category||"").trim().toUpperCase()) && (
                        <span style={{ background:"#dcfce7", color:"#166534", padding:"2px 8px", borderRadius:20, fontSize:10, fontWeight:700, letterSpacing:0.5 }}>CONSUMABLE</span>
                      )}
                    </div>
                    {item.is_custom_item && item.item_status !== "redirected_to_asset_registry" && (() => {
                      const TOOL_KEYWORDS = ["drill","machine","cutter","grinder","ladder","pump","gauge","meter","blower","cylinder","vacuum","welding","compressor","tester","wrench","spanner","saw","hammer","screwdriver","plier","tool","equipment","device"];
                      const nameLower = (item.item_name || "").toLowerCase();
                      const looksLikeTool = TOOL_KEYWORDS.some(kw => nameLower.includes(kw));

                      if (looksLikeTool) {
                        return (
                          <div style={{ display:"flex", flexDirection:"column", gap:6, marginTop:4, padding:"8px 10px", background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:10 }}>
                            <span style={{ display:"inline-block", width:"fit-content", background:"#FEE2E2", color:"#991B1B", padding:"3px 8px", borderRadius:20, fontSize:11, fontWeight:700 }}>🔧 LOOKS LIKE A TOOL / ASSET</span>
                            <span style={{ fontSize:11, color:"#7F1D1D", lineHeight:1.5 }}>
                              This item cannot be issued from stock — tools are tracked via the Asset Registry and transferred to technicians, not issued through MRF.
                            </span>
                            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                              <button
                                onClick={async () => {
                                  try {
                                    const user = JSON.parse(localStorage.getItem("user") || "{}");
                                    const { doc: fsDoc, getDoc, updateDoc: fsUpdate, serverTimestamp: fsST } = await import("firebase/firestore");
                                    // Find item_request for this item and reject it
                                    const { getDocs, query, where, collection: fsCol } = await import("firebase/firestore");
                                    const reqSnap = await getDocs(query(
                                      fsCol(db, "item_requests"),
                                      where("company_id", "==", mrf.company_id || ""),
                                      where("item_name_lower", "==", (item.item_name || "").toLowerCase().trim()),
                                      where("status", "==", "pending")
                                    ));
                                    for (const reqDoc of reqSnap.docs) {
                                      await fsUpdate(fsDoc(db, "item_requests", reqDoc.id), {
                                        status: "rejected",
                                        reject_reason: "Tool/Asset item — redirected to Asset Registry.",
                                        rejected_at: fsST(),
                                        rejected_by: user.name || "",
                                      });
                                    }
                                  } catch (e) {
                                    console.warn("Item request reject failed (non-blocking):", e);
                                  }
                                  navigate("/assets", {
                                    state: {
                                      prefill_name:      item.item_name,
                                      from_item_request: true,
                                      from_mrf:          true,
                                      requested_by:      mrf.requested_by_name || "",
                                    }
                                  });
                                }}
                                style={{ padding:"6px 14px", background:"#7C3AED", color:"#fff", border:"none", borderRadius:8, fontSize:11, fontWeight:600, cursor:"pointer" }}>
                                🔧 Add to Asset Registry & Reject Item Request →
                              </button>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                          <span style={{ display:"inline-block", width:"fit-content", background:"#fef3c7", color:"#92400e", padding:"3px 8px", borderRadius:20, fontSize:11, fontWeight:700 }}>⚠ CUSTOM ITEM</span>
                          <span style={{ fontSize:11, color:"#6b7280" }}>This item is not yet in the Item Master. Approve it in Item Requests before it can be procured.</span>
                          <button onClick={() => navigate("/item-requests")} style={{ display:"inline-block", width:"fit-content", padding:"3px 10px", background:"#ede9fe", color:"#5b21b6", border:"none", borderRadius:8, fontSize:11, fontWeight:600, cursor:"pointer" }}>📋 Review in Item Requests →</button>
                        </div>
                      );
                    })()}
                    {item.item_status === "redirected_to_asset_registry" && (
                      <div style={{ display:"flex", flexDirection:"column", gap:6, marginTop:4 }}>
                        <span style={{ display:"inline-block", width:"fit-content", background:"#F3E8FF", color:"#6B21A8", padding:"3px 8px", borderRadius:20, fontSize:11, fontWeight:700 }}>🔧 TOOL / ASSET</span>
                        <span style={{ fontSize:11, color:"#6b7280" }}>
                          Redirected to Asset Registry. Transfer it to the technician from there.
                          {item.redirected_by && ` — by ${item.redirected_by}`}
                        </span>
                        <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                          <button
                            onClick={() => navigate("/assets/transfer", {
                              state: { prefill_asset_name: item.asset_name || item.item_name }
                            })}
                            style={{ padding:"4px 10px", background:"#2563EB", color:"#fff", border:"none", borderRadius:8, fontSize:11, fontWeight:600, cursor:"pointer" }}>
                            ↗ Transfer Tool to Technician →
                          </button>
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Mark "${item.item_name}" as issued? Only do this after transfer to technician is confirmed.`)) return;
                              try {
                                const updatedItems = mrf.items.map((it, idx) =>
                                  idx === i
                                    ? { ...it, item_status: "issued", issued_qty: 1, remaining_qty: 0, manually_issued_at: new Date().toISOString() }
                                    : it
                                );
                                const allDone = updatedItems.every(it =>
                                  ["issued","cancelled","redirected_to_asset_registry"].includes(it.item_status) ||
                                  it.item_status === "issued"
                                );
                                await updateDoc(doc(db, "material_requests", id), {
                                  items: updatedItems,
                                  status: allDone ? "issued" : mrf.status,
                                  updated_at: serverTimestamp(),
                                });
                                await refreshMRFScreen();
                              } catch (err) {
                                alert("Failed: " + err.message);
                              }
                            }}
                            style={{ padding:"4px 10px", background:"#16a34a", color:"#fff", border:"none", borderRadius:8, fontSize:11, fontWeight:600, cursor:"pointer" }}>
                            ✅ Mark Tool as Issued →
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </td>
                <td style={td}>{item.unit || item.uom || "nos"}</td>
                <td style={td}>{item.qty}</td>
                <td style={td}>
                  {mrf.status === "issued" ? item.approved_qty : (
                    <input type="number" min="0" value={item.approved_qty}
                      onChange={e => handleItemChange(i, "approved_qty", e.target.value)}
                      style={{ ...input,
                        border: Number(inventoryMap[String(item.item_id||"").trim()]||0) < Number(item.approved_qty||item.qty||0) ? "1px solid #dc2626" : "1px solid #ccc",
                        color:  Number(inventoryMap[String(item.item_id||"").trim()]||0) < Number(item.approved_qty||item.qty||0) ? "#dc2626" : "#111827",
                        fontWeight:600,
                      }}
                    />
                  )}
                </td>
                <td style={td}>{item.is_custom_item ? "--" : Number(inventoryMap?.[String(item.item_id||"").trim()]||0)}</td>
                <td style={td}>{Number(item.issued_qty||item.issuedQuantity||item.issue_qty||item.issued||0)}</td>
                <td style={td}>
                  {item.item_status ? (
                    <span style={{
                      fontSize:10, fontWeight:700, padding:"3px 8px", borderRadius:20,
                      background: item.item_status==="issued" ? "#DCFCE7" : item.item_status==="approved_pending_issue" ? "#DBEAFE" : item.item_status==="procurement_pending" ? "#FEF3C7" : item.item_status==="partially_issued" ? "#FDE68A" : item.item_status==="cancelled" ? "#FEE2E2" : item.item_status==="redirected_to_asset_registry" ? "#F3E8FF" : "#F3F4F6",
                      color:      item.item_status==="issued" ? "#166534" : item.item_status==="approved_pending_issue" ? "#1D4ED8" : item.item_status==="procurement_pending" ? "#92400E" : item.item_status==="partially_issued" ? "#78350F" : item.item_status==="cancelled" ? "#991B1B" : item.item_status==="redirected_to_asset_registry" ? "#6B21A8" : "#374151",
                    }}>
                      {item.item_status.replace(/_/g," ")}
                    </span>
                  ) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ marginTop:20, display:"flex", gap:10, flexWrap:"wrap" }}>
          {["pending"].includes(mrf.status) && (
            <>
              <button style={btnPrimary} onClick={handleApprove}>Approve</button>
              <button style={btnDanger}  onClick={handleReject}>Reject</button>
            </>
          )}

          {["approved","partially_issued"].includes(mrf.status) &&
           mrf.request_type === "tool" &&
           (mrf?.items||[]).some(i => i.is_tool && Number(i.issued_qty||0) < 1) && (
            <button style={{ ...btnPrimary, background:"linear-gradient(135deg,#7c3aed,#6d28d9)", boxShadow:"0 4px 12px rgba(124,58,237,0.25)", opacity:issuingTools?0.6:1, cursor:issuingTools?"not-allowed":"pointer" }} onClick={handleIssueTool} disabled={issuingTools}>
              {issuingTools ? "Issuing Tools..." : "🔧 Issue Tools"}
            </button>
          )}

          {["approved","partially_issued"].includes(mrf.status) &&
           (mrf?.items||[]).some(item => {
             const cat = String(item.category||"").trim().toUpperCase();
             if (cat==="TOOLS"||cat==="ASSET") return false;
             return item.item_status==="approved_pending_issue" || item.item_status==="partially_issued";
           }) && (
            <button style={{ ...btnPrimary, opacity:issuing?0.6:1, cursor:issuing?"not-allowed":"pointer" }} onClick={handleIssue} disabled={issuing}>
              {issuing ? "Issuing..." : "Issue Material"}
            </button>
          )}

          {["approved","partially_issued"].includes(mrf.status) &&
           (mrf?.items||[]).some(item => ["partially_issued","procurement_pending","approved_pending_issue"].includes(item.item_status)) && (
            <button style={{ ...btnPrimary, background:"linear-gradient(135deg,#d97706,#b45309)" }}
              onClick={async () => {
                const confirm = window.confirm("Close MRF? Remaining unissued quantity will be cancelled. Only issued items will be tracked.");
                if (!confirm) return;
                try {
                  const closedItems = mrf.items.map(item => ({
                    ...item,
                    item_status:   ["issued"].includes(item.item_status) ? "issued" : Number(item.issued_qty||0) > 0 ? "partially_issued" : "cancelled",
                    remaining_qty: 0,
                  }));
                  await updateDoc(doc(db, "material_requests", id), {
                    status:"issued", items:closedItems,
                    closed_by:      JSON.parse(localStorage.getItem("user")||"{}").uid||"",
                    closed_by_name: JSON.parse(localStorage.getItem("user")||"{}").name||"",
                    closed_at: serverTimestamp(), updated_at: serverTimestamp(),
                  });
                  await refreshMRFScreen();
                  alert("✅ MRF Closed — remaining qty cancelled");
                } catch (err) { alert("Close failed: " + err.message); }
              }}>
              🔒 Close MRF
            </button>
          )}
        </div>
      </div>

      {mrf.reminder_required && mrf.reminder_status !== "done" && (
        <div style={{ ...card, background:"#fff8e1" }}>
          <p>⚠ Pending Material Issue</p>
          <button style={btnPrimary} onClick={handleReminderDone}>Done</button>
          <button style={btn}        onClick={handleRemindLater}>Remind Later</button>
        </div>
      )}

      {showVariationModal && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:50, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
          <div style={{ background:"#fff", borderRadius:16, width:"100%", maxWidth:560, maxHeight:"90vh", overflowY:"auto", padding:24 }}>
            <h2 style={{ fontSize:18, fontWeight:700, marginBottom:8, color:"#111827" }}>⚠️ Budget Exceeded</h2>
            <p style={{ fontSize:13, color:"#6b7280", marginBottom:16 }}>
              Issuing this MRF will push the following item(s) past their stage's budgeted quantity.
              This will be recorded as a variation and the stage budget will be adjusted to match.
            </p>
            <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:16 }}>
              {pendingVariations.map((v, i) => (
                <div key={i} style={{
                  padding:"10px 14px",
                  background: v.is_major ? "#fef2f2" : "#fffbeb",
                  border: `1px solid ${v.is_major ? "#fecaca" : "#fde68a"}`,
                  borderRadius:10,
                }}>
                  <div style={{ fontWeight:600, fontSize:13, color:"#111827" }}>{v.item_name} — {v.stage_name}</div>
                  <div style={{ fontSize:12, color:"#6b7280", marginTop:2 }}>
                    Exceeds by {v.exceeded_qty} {v.unit} (+₹{Number(v.extra_cost).toLocaleString("en-IN")})
                    {v.is_major && <span style={{ color:"#991b1b", fontWeight:700 }}> — requires Owner/Manager approval</span>}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:"#6b7280", display:"block", marginBottom:4 }}>Approved by (name) *</label>
                <input value={variationForm.approved_by_name}
                  onChange={e => setVariationForm(f => ({ ...f, approved_by_name: e.target.value }))}
                  style={{ width:"100%", padding:"8px 10px", border:"1px solid #d1d5db", borderRadius:8, fontSize:13 }} />
              </div>
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:"#6b7280", display:"block", marginBottom:4 }}>Approval channel</label>
                <select value={variationForm.approval_channel}
                  onChange={e => setVariationForm(f => ({ ...f, approval_channel: e.target.value }))}
                  style={{ width:"100%", padding:"8px 10px", border:"1px solid #d1d5db", borderRadius:8, fontSize:13, background:"#fff" }}>
                  <option value="phone">Phone call</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="email">Email</option>
                  <option value="in_person">In person</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:"#6b7280", display:"block", marginBottom:4 }}>Note *</label>
                <input value={variationForm.approval_note}
                  onChange={e => setVariationForm(f => ({ ...f, approval_note: e.target.value }))}
                  placeholder="Why the extra quantity was needed..."
                  style={{ width:"100%", padding:"8px 10px", border:"1px solid #d1d5db", borderRadius:8, fontSize:13 }} />
              </div>
            </div>
            <div style={{ display:"flex", gap:10, marginTop:20 }}>
              <button style={{ ...btn, flex:1 }}
                onClick={() => { setShowVariationModal(false); setPendingVariations([]); }}>
                Cancel
              </button>
              <button style={{ ...btnPrimary, flex:1, opacity: variationSaving ? 0.6 : 1, cursor: variationSaving ? "not-allowed" : "pointer" }}
                onClick={handleConfirmVariation} disabled={variationSaving}>
                {variationSaving ? "Recording..." : "Record & Continue Issue →"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* STYLES */
const card = { background:"#fff", padding:28, borderRadius:16, marginBottom:20, boxShadow:"0 8px 24px rgba(0,0,0,0.08)" };
const td   = { padding:16, borderBottom:"1px solid #eee" };
const input = { width:"80px", padding:"6px", borderRadius:6, border:"1px solid #ccc" };
const btn   = { padding:"8px 14px", background:"#e0e0e0", border:"none", borderRadius:6, cursor:"pointer" };
const btnPrimary = { padding:"10px 18px", background:"linear-gradient(135deg,#2563eb,#1d4ed8)", color:"#fff", border:"none", borderRadius:10, cursor:"pointer", fontWeight:600, boxShadow:"0 4px 12px rgba(37,99,235,0.25)" };
const btnDanger  = { padding:"10px 18px", background:"linear-gradient(135deg,#dc2626,#b91c1c)", color:"#fff", border:"none", borderRadius:10, cursor:"pointer", fontWeight:600 };
const statusBadge = (status) => ({
  marginLeft:10, padding:"5px 12px", borderRadius:20, fontWeight:"600",
  fontSize:"13px", textTransform:"capitalize",
  color:      status==="approved" ? "#065f46" : status==="issued" ? "#1d4ed8" : status==="partially_issued" ? "#92400e" : status==="cancelled" ? "#991b1b" : "#92400e",
  background: status==="approved" ? "#d1fae5" : status==="issued" ? "#dbeafe" : status==="partially_issued" ? "#fde68a" : status==="cancelled" ? "#fee2e2" : "#fef3c7",
});